import {
    IonButton,
    IonCard,
    IonCardContent,
    IonCol,
    IonIcon,
    IonLabel,
    IonRefresher,
    IonRefresherContent,
    IonRouterLink,
    IonRow,
    IonText,
    IonToggle,
    RefresherEventDetail,
    useIonToast,
} from '@ionic/react';
import { Trans, t } from '@lingui/macro';
import { Form, Formik, FormikHelpers } from 'formik';
import { home } from 'ionicons/icons';
import { useAtom } from 'jotai';
import { getDefaultStore } from 'jotai/index';
import React, { memo, useCallback, useMemo, useState } from 'react';
import * as Yup from 'yup';

import SmartMeter from '../../../components/SmartMeter';
import LoadableContent from '../../../components/content/LoadableContent';
import DocumentViewerModal from '../../../components/documentviewermodal/DocumentViewerModal';
import PDLForm from '../../../components/forms/pdl/PDLForm';
import LoadingButton from '../../../components/loadingbutton/LoadingButton';
import InterventionPage from '../../../components/page/InterventionPage';
import PhotoUpload from '../../../components/photoUpload/PhotoUpload';
import { getTransServerMessageString } from '../../../components/translations/TransServerMessage';
import { IJob } from '../../../interfaces/IJob';
import { IInstallationRouteProps } from '../../../interfaces/IRoute';
import {
    accessTokenAtom,
    canPerformInstallationAtom,
    generalTermsAtom,
    installationInspectLoadableAtom,
    jobIdAtom,
    pdlValidatedAtom,
    tabsEnabledAtom,
    updateDBStateAtom,
} from '../../../store/Atoms';
import {
    acceptGeneralTermsRequest,
    inspectRequest,
} from '../../../store/Requests';
import { DeepomaticTaskGroups } from '../../../types/Generic';
import { ImageCategories } from '../../../types/ImageInfo';
import { MAX_IMAGE_NR } from '../../../utils/constants';
import { useCustomRouter } from '../../../utils/hooks/useCustomRouter';
import usePhotosUploaded from '../../../utils/hooks/usePhotosUploaded';

type InspectFormValues = {
    hasSmartMeter: boolean;
    hasSmartMeterSealed: boolean;
    meteringPointNumber: string;
};

const InspectSchema = () =>
    Yup.object().shape({
        hasSmartMeter: Yup.boolean(),
        hasSmartMeterSealed: Yup.boolean(),
        meteringPointNumber: Yup.string().required(t`PDL is required`),
    });

const defaultStore = getDefaultStore();

const Inspect: React.FC<IInstallationRouteProps> = memo(() => {
    const router = useCustomRouter();
    const [presentToast] = useIonToast();
    const photosUploaded = usePhotosUploaded();
    const [jobId] = useAtom(jobIdAtom);
    const [value, refresh] = useAtom(installationInspectLoadableAtom);
    const [canPerformInstallation, setCanPerformInstallation] = useAtom(
        canPerformInstallationAtom
    );
    const [accessToken] = useAtom(accessTokenAtom);
    const [pdlValidated] = useAtom(pdlValidatedAtom);
    const [, setTabsEnabled] = useAtom(tabsEnabledAtom);
    const [, updateDBState] = useAtom(updateDBStateAtom);
    const [showModal, setShowModal] = useState(false);
    // For YesNo
    //const [gta, setGTA] = useState<boolean | undefined>(undefined);
    const [gta, setGTA] = useState<boolean>(false);

    const stateGTAJobDetails:
        | {
              job: IJob;
              gtCguAgreed: boolean;
          }
        | undefined = useMemo(
        () => (value.state === 'hasData' ? value.data : undefined),
        [value]
    );

    const job = stateGTAJobDetails?.job;
    const agbAttachmentId =
        job?.organisationalUnit[2]?.defaultAgb[0]?.agbAttachmentId;
    const gtCguAgreed = stateGTAJobDetails?.gtCguAgreed;
    const gtCguAgreement = gtCguAgreed ? gtCguAgreed : gta;
    const enableProceed =
        canPerformInstallation &&
        pdlValidated &&
        gtCguAgreement &&
        photosUploaded;

    const gtaLabelStyle = { lineHeight: '1.5' };
    const gtaYesMessage = gta && (
        <IonRow className="ion-justify-content-between ion-align-items-center ion-margin-top ion-margin-bottom">
            <IonCol>
                <IonLabel style={gtaLabelStyle}>
                    <Trans>
                        I{job && `, ${job.contactName}, `}accept the general
                        terms of agreement.
                    </Trans>
                </IonLabel>
            </IonCol>
        </IonRow>
    );

    // For YesNo
    // const gtaNoMessage = !gta && gta !== undefined && (

    const gtaNoMessage = !gta && (
        <IonRow className="ion-justify-content-between ion-align-items-center ion-margin-top ion-margin-bottom">
            <IonCol>
                <IonLabel style={gtaLabelStyle}>
                    <Trans>
                        Please show the customer the General Terms of Agreement
                        and he needs to accept them to proceed.
                    </Trans>
                    <br />
                    <br />
                    <Trans>
                        If he does not, click "I can't perform the
                        installation?" and on the next page select the Reason{' '}
                        {'>'} Client refused to sign the contract.
                    </Trans>
                </IonLabel>
            </IonCol>
        </IonRow>
    );

    const signGTA = useCallback(async () => {
        try {
            await acceptGeneralTermsRequest(accessToken, jobId);
            defaultStore.set(generalTermsAtom, true as any);
        } catch (e) {
            throw e;
        }
    }, [accessToken, jobId]);

    const handleFormSubmit = useCallback(
        async (
            data: InspectFormValues,
            actions: FormikHelpers<InspectFormValues>
        ) => {
            actions.setSubmitting(true);
            try {
                await signGTA();
                await inspectRequest(
                    accessToken,
                    jobId,
                    data.meteringPointNumber,
                    data.hasSmartMeter,
                    data.hasSmartMeterSealed
                );
                setTabsEnabled((prevState) => {
                    return { ...prevState, wire: true };
                });
                await updateDBState();
                router.push('wire', 'none');
            } catch (e: any) {
                const errMsg =
                    e.reason || e.message || t`Oops, something went wrong!`;
                const errorMessage = getTransServerMessageString(errMsg);
                presentToast({
                    message: errorMessage,
                    duration: 5000,
                    color: 'danger',
                    buttons: [
                        {
                            text: t`Dismiss`,
                            role: 'cancel',
                        },
                    ],
                });
            }
            actions.setSubmitting(false);
        },
        [
            accessToken,
            jobId,
            presentToast,
            router,
            setTabsEnabled,
            signGTA,
            updateDBState,
        ]
    );

    const handleRefresh = useCallback(
        async (event: CustomEvent<RefresherEventDetail>) => {
            try {
                await refresh();
            } catch (e: any) {
                presentToast({
                    message: t`Failed to refresh the page! Please try again.`,
                    duration: 5000,
                    color: 'danger',
                    buttons: [
                        {
                            text: t`Dismiss`,
                            role: 'cancel',
                        },
                    ],
                });
            }
            event.detail.complete();
        },
        [presentToast, refresh]
    );

    return (
        <InterventionPage title={t`Inspect`} withPadding>
            <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                <IonRefresherContent refreshingSpinner="circular"></IonRefresherContent>
            </IonRefresher>
            <LoadableContent value={value}>
                <Formik
                    initialValues={{
                        hasSmartMeter: job?.hasSmartMeter || false,
                        hasSmartMeterSealed: job?.hasSmartMeterSealed || false,
                        meteringPointNumber: job?.meteringPointNumber || '',
                    }}
                    validationSchema={InspectSchema()}
                    onSubmit={handleFormSubmit}
                >
                    {({ values, isSubmitting, setFieldValue }) => (
                        <Form>
                            <IonCard className="ion-margin-bottom">
                                <IonCardContent>
                                    <IonText>
                                        <IonIcon
                                            icon={home}
                                            size="large"
                                            className="ion-float-left ion-padding-end"
                                        />
                                        <Trans>Installation inspection</Trans>
                                        <br />
                                        <Trans>
                                            Please provide basic information on
                                            the home and verify the feasibility
                                            of the installation.
                                        </Trans>
                                    </IonText>
                                    &nbsp;
                                    <IonText className="ion-padding-top">
                                        <Trans>
                                            You can help the client to download
                                            the app in order to save time.
                                            Please note, they will only be able
                                            to connect after the end of the
                                            installation.
                                        </Trans>
                                    </IonText>
                                </IonCardContent>
                            </IonCard>
                            {!gtCguAgreed && (
                                <IonRow className="ion-justify-content-between ion-align-items-center ion-margin-top ion-margin-bottom">
                                    <IonCol>
                                        <IonLabel>
                                            <Trans>
                                                General Terms of Agreement
                                            </Trans>{' '}
                                            <>
                                                (
                                                {agbAttachmentId ? (
                                                    <>
                                                        <IonRouterLink
                                                            onClick={() =>
                                                                setShowModal(
                                                                    !showModal
                                                                )
                                                            }
                                                        >
                                                            <Trans>GTA</Trans>
                                                        </IonRouterLink>
                                                        <DocumentViewerModal
                                                            isOpen={showModal}
                                                            onModalClosed={() =>
                                                                setShowModal(
                                                                    !showModal
                                                                )
                                                            }
                                                            documentName={
                                                                agbAttachmentId
                                                            }
                                                        />
                                                    </>
                                                ) : (
                                                    <span>
                                                        <Trans>
                                                            document not found
                                                        </Trans>
                                                    </span>
                                                )}
                                                )
                                            </>
                                        </IonLabel>
                                    </IonCol>
                                    <IonCol size="auto">
                                        <IonToggle
                                            color="primary"
                                            onIonChange={(e) =>
                                                setGTA(e.detail.checked)
                                            }
                                            checked={gta}
                                            data-tiko-test="gta-switch"
                                        />
                                    </IonCol>
                                    {gtaYesMessage}
                                    {gtaNoMessage}
                                </IonRow>
                            )}
                            <SmartMeter
                                hasSmartMeter={values.hasSmartMeter}
                                hasSmartMeterSealed={
                                    values.hasSmartMeterSealed
                                }
                                onHasSmartMeterChange={(hasSmartMeter) => {
                                    setFieldValue(
                                        'hasSmartMeter',
                                        hasSmartMeter
                                    );
                                }}
                                onHasSmartMeterSealedChange={(
                                    hasSmartMeterSealed
                                ) => {
                                    setFieldValue(
                                        'hasSmartMeterSealed',
                                        hasSmartMeterSealed
                                    );
                                }}
                            />
                            <PDLForm
                                pdl={
                                    pdlValidated
                                        ? values.meteringPointNumber
                                        : ''
                                }
                                onChange={(pdl) =>
                                    setFieldValue('meteringPointNumber', pdl)
                                }
                                preFilledPdl={job?.meteringPointNumber}
                            />
                            <PhotoUpload
                                className="ion-margin-bottom"
                                imageCode={
                                    ImageCategories.photosCustomerNumber
                                }
                                maxNumber={MAX_IMAGE_NR}
                                label={t`Photo of customer PDL`}
                                mandatory={true}
                                deepomaticTaskGroup={DeepomaticTaskGroups.PDL}
                                deepomaticWorkOrderId={
                                    job?.imageClassificationWorkOrderId
                                }
                            ></PhotoUpload>
                            <PhotoUpload
                                className="ion-margin-bottom"
                                imageCode={
                                    ImageCategories.photosBeforeInstallation
                                }
                                maxNumber={MAX_IMAGE_NR}
                                label={t`Photo before installation`}
                                deepomaticTaskGroup={
                                    DeepomaticTaskGroups.ELECTRICAL_PANEL_BEFORE
                                }
                                deepomaticWorkOrderId={
                                    job?.imageClassificationWorkOrderId
                                }
                            ></PhotoUpload>
                            <PhotoUpload
                                imageCode={
                                    ImageCategories.safetyRisksOrAnomalies
                                }
                                maxNumber={MAX_IMAGE_NR}
                                label={t`Photo of risks or anomalies`}
                            ></PhotoUpload>
                            <IonRow className="ion-justify-content-between ion-align-items-center ion-margin-top ion-margin-bottom">
                                <IonCol>
                                    <IonLabel
                                        style={
                                            !gtCguAgreement
                                                ? { opacity: 0.3 }
                                                : {}
                                        }
                                    >
                                        <Trans>
                                            Can you perform the installation?
                                        </Trans>
                                    </IonLabel>
                                </IonCol>
                                <IonCol size="auto">
                                    <IonToggle
                                        color="primary"
                                        onIonChange={(e) =>
                                            setCanPerformInstallation(
                                                e.detail.checked
                                            )
                                        }
                                        checked={
                                            !gtCguAgreement
                                                ? false
                                                : canPerformInstallation
                                        }
                                        disabled={!gtCguAgreement}
                                        data-tiko-test="can-perform-installation-switch"
                                    />
                                </IonCol>
                            </IonRow>
                            {!canPerformInstallation || !gtCguAgreement ? (
                                <IonButton
                                    expand="block"
                                    routerLink={`/${jobId}/installation/inspect/done-error`}
                                    routerDirection="forward"
                                >
                                    {t`I can't perform the installation`}
                                </IonButton>
                            ) : (
                                <LoadingButton
                                    disabled={!enableProceed}
                                    loading={isSubmitting}
                                    type="submit"
                                    data-tiko-test="proceed-installation-button"
                                >
                                    {!pdlValidated
                                        ? t`Validate PDL to Proceed`
                                        : t`Proceed with installation`}
                                </LoadingButton>
                            )}
                        </Form>
                    )}
                </Formik>
            </LoadableContent>
        </InterventionPage>
    );
});

export default Inspect;
